/**
 * flujos.api.ts — Wrappers tipados de los endpoints del módulo Flujos.
 *
 * Round 2026-05-13 — el módulo /flujos reemplaza al /gastos legacy y
 * agrupa todos los movimientos de caja de AF Empresa: gastos (AF paga),
 * ingresos (AF recibe) y intermediarios (AF entrega y recibe el mismo
 * monto). Los 3 tipos viven en la misma tabla `gastos` (Expense) en
 * Postgres — solo cambia el `rol_de_aloja_facil`.
 *
 * El módulo es solo frontend (UI nueva); los endpoints de backend
 * (`/api/finanzas/empresa/expenses`) se mantienen sin cambios.
 */

import { apiFetch } from "../../services/apiClient";

// ── Tipos compartidos ──────────────────────────────────────────────────────

/**
 * Códigos de `tipo` para `pagado_por`/`pagado_a` (JSONB en DB).
 *   0 = AF Empresa
 *   1 = Propietario / Cliente
 *   2 = Arrendatario / Huésped
 *   3 = Aseador
 *   4 = Trabajador (mantención, supervisor, contador, etc.)
 *   5 = Receptor externo: OTRA EMPRESA que recibe plata de AF y no es
 *       usuaria del sistema (catálogo `expense_recipients`; el modelo
 *       Expense exige `recipient_id` cuando tipo=5). Ej.: Movistar,
 *       Aguas Andinas, el SII, una tienda de insumos o el receptor
 *       semilla "Otro". NUNCA es una lavandería: los pagos a lavandería
 *       (AF interna o Lavandería General Externa) se registran en el
 *       módulo /lavanderia sobre la tabla `lavados`, jamás como gasto a
 *       un receptor externo — hacerlo los vuelve invisibles para la
 *       reconciliación de /lavanderia y los cuenta dos veces en la
 *       utilidad. Ver docs/flujo-financiero.md §10.2.
 */
export type FlujoPartyTipo = 0 | 1 | 2 | 3 | 4 | 5;

export interface FlujoParty {
  tipo: FlujoPartyTipo;
  nombre: string;
  usuario_id?: number | null;
  recipient_id?: number | null;
}

/** Modo de creación del flujo desde la UI. Determina el preset del modal. */
export type FlujoPresetMode = "gasto" | "ingreso" | "intermediario";

/** Categorías visibles en el modal de /flujos. Round 2026-05-13: se
 *  agregaron `bono` y `multa` al ENUM `enum_gastos_categoria` para
 *  clasificar movimientos extra al aseador desde el panel ejecutivo.
 *  Round 2026-05-31: se agregó `aseo` para clasificar gastos manuales
 *  de aseo (extras al sistema automatizado de aseos AUTO — ej. compra
 *  de insumos puntuales, transporte adicional, contrato externo
 *  específico). El ENUM `expenses.categoria` del backend ya lo
 *  aceptaba (modelo expenses.js); este array solo expone la opción
 *  en el dropdown del modal. NO se confunde con los aseos AUTO del
 *  sistema (tabla `aseos`) — esto es un GASTO MANUAL contable. */
export const FLUJO_CATEGORIAS = [
  "reparacion",
  "cuenta",
  "aseo",
  "compra",
  "transporte",
  "emergencia",
  "publicidad",
  "arriendo",
  "computacion",
  "fotografia",
  "supervision",
  "impuestos",
  "reembolso",
  "bono",
  "multa",
  "otros",
] as const;

/** Etiqueta legible (con tildes) para mostrar en la UI a partir de la
 *  clave canónica que va a la DB (sin tilde, lowercase). */
export const CATEGORIA_LABEL: Record<string, string> = {
  reparacion: "Reparación",
  cuenta: "Cuenta",
  aseo: "Aseo",
  compra: "Compra",
  transporte: "Transporte",
  emergencia: "Emergencia",
  publicidad: "Publicidad",
  arriendo: "Arriendo",
  computacion: "Computación",
  fotografia: "Fotografía",
  supervision: "Supervisión",
  impuestos: "Impuestos",
  reembolso: "Reembolso",
  bono: "Bono",
  multa: "Multa",
  otros: "Otros",
};

export type FlujoCategoria = (typeof FLUJO_CATEGORIAS)[number];

/**
 * Registro de flujo tal como lo devuelve el backend. Mismo shape que el
 * `expensesToFrontend` original (compat con /finanzas-empresa).
 */
export interface Flujo {
  /**
   * PK de `gastos`. Es un NÚMERO en runtime (columna INTEGER autoincremental;
   * el backend la emite cruda en `mapExpense`). Estuvo declarada como `string`
   * hasta el 2026-07-26: como TypeScript no valida el JSON de la API, la
   * mentira no daba error de compilación pero rompía en silencio TODA
   * comparación entre `id` y `pairedId` (que sí es `number`) — el `Map` de
   * flujos quedaba con claves numéricas y se consultaba con claves string, así
   * que el par de un intermediario NUNCA se resolvía. Ver §6 de
   * docs/modulo-finanzas-empresa.md.
   */
  id: number;
  /** "esporadico" = fecha puntual; "regular" = se repite mensualmente el día N. */
  tipo: "esporadico" | "regular";
  monto: number;
  fecha?: string | null;
  fechaPeriodica?: number | null;
  /**
   * Vigencia del flujo REGULAR (round 2026-07-26). El gasto solo rige —y solo
   * suma— dentro de este rango, con granularidad de MES.
   * `vigenciaInicio` NULL = "sin vigencia registrada": regular anterior al
   * round; se comporta como antes (cuenta siempre) hasta que un humano la
   * defina. `vigenciaFin` NULL = indefinido.
   */
  vigenciaInicio?: string | null;
  vigenciaFin?: string | null;

  pagadoPor: FlujoParty | null;
  pagadoA: FlujoParty | null;

  /**
   * Rol AF — fuente de verdad del tipo del flujo en DB.
   *   "paga"          → Gasto (AF egresa)
   *   "recibe"        → Ingreso (AF cobra)
   *   "intermediario" → AF en el medio (entra y sale por el mismo monto)
   */
  rolDeAlojaFacil: "paga" | "recibe" | "intermediario";

  categoria: FlujoCategoria | string;
  concepto?: string | null;
  estadoPago?: string | null;
  descripcion?: string | null;
  nombre?: string | null;
  propiedadId?: number | null;
  /** Reembolso cuyo costo ya se registró en esta lavandería. */
  lavanderiaId?: number | null;
  imagenes?: Array<Partial<FlujoFile> | string> | null;
  /**
   * Id del row "par" cuando este flujo es la mitad de un intermediario
   * (2 rows enlazados con cross-link, round 2026-05-13). NULL para
   * gastos/ingresos sueltos o intermediarios legacy 1-row.
   */
  pairedId?: number | null;
  /**
   * Id del row en `inventario_transacciones` que originó este flujo
   * (round 2026-06-01, ver docs/flujo-financiero.md §10.8). NO NULL =
   * flujo auto-generado desde una compra del módulo /inventario.
   * Implica:
   *   - Badge 📦 Producto en la grilla.
   *   - Monto / categoría READ-ONLY en el modal (la fuente de verdad
   *     es el Inventory row; editar desde /inventario propaga).
   *   - Borrado bloqueado desde /flujos (admin debe ir al producto).
   */
  inventoryTxId?: number | null;
}

/** Payload para crear un flujo. El backend acepta tanto el JSON completo
 *  de `pagadoPor`/`pagadoA` como los campos sueltos `pagadoPorRol`/etc.
 *  Acá siempre enviamos el JSON completo. */
export interface FlujoCreatePayload {
  monto: number;
  tipo: "esporadico" | "regular";
  fecha?: string | null;
  fechaPeriodica?: number | null;
  /** Solo para regulares. Inicio obligatorio; fin null = indefinido. */
  vigenciaInicio?: string | null;
  vigenciaFin?: string | null;

  pagadoPor: FlujoParty;
  pagadoA: FlujoParty;

  rolDeAlojaFacil: "paga" | "recibe" | "intermediario";

  categoria: FlujoCategoria | string;
  descripcion?: string;
  nombre?: string;
  propiedadId?: number | null;
  imagenes?: string[];
}

export type FlujoUpdatePayload = Partial<FlujoCreatePayload>;

// ── Endpoints ──────────────────────────────────────────────────────────────

/**
 * Listado de flujos del período. El backend solo expone búsqueda
 * paginada vía `POST /api/finanzas/empresa/gastos/search` con filtros
 * (no hay `GET /expenses` listo).
 *
 * Acá iteramos paginando con page_size grande (200, el máximo del
 * backend) para traer todos los registros del rango de fechas.
 * El filtrado por tipo (rolAF) y categoría se hace client-side.
 *
 * @param fromISO  Fecha inicio ISO (YYYY-MM-DD). Si está undefined,
 *                 no se aplica filtro inferior.
 * @param toISO    Fecha fin ISO (YYYY-MM-DD). Si está undefined, no se
 *                 aplica filtro superior.
 */
export async function listarFlujos(
  fromISO?: string,
  toISO?: string,
): Promise<Flujo[]> {
  const all: Flujo[] = [];
  let page = 1;
  const pageSize = 200;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const body: any = {
      page,
      page_size: pageSize,
      filters: { fields: {} },
    };
    // Si tenemos rango, lo enviamos como `globalDateRange` para que el
    // backend lo aplique sobre `fecha` y `fecha_periodica` (helper
    // `buildExpensesGlobalDateOr`).
    if (fromISO && toISO) {
      body.globalDateRange = [fromISO, toISO];
    }
    const res = await apiFetch<{ data: Flujo[]; total: number }>(
      "/finanzas/empresa/gastos/search",
      { method: "POST", body: JSON.stringify(body) },
    );
    if (!res) break;
    const rows = Array.isArray(res.data) ? res.data : [];
    all.push(...rows);
    if (rows.length < pageSize) break;
    if (all.length >= (res.total ?? 0)) break;
    page += 1;
    // Hard stop para evitar bucles infinitos por bug del backend.
    if (page > 50) break;
  }
  return all;
}

/**
 * Pair de rows del intermediario tal como lo devuelve el backend.
 * `ingreso` = lado 'recibe' (AF cobra del origen externo).
 * `gasto`   = lado 'paga' (AF entrega al destino externo).
 */
export interface FlujoPair {
  ingreso: Flujo;
  gasto: Flujo;
}

/**
 * Respuesta del create / update. En modo intermediario incluye `pair`
 * con ambos rows del par; en gasto/ingreso suelto `pair` viene undefined.
 */
export interface FlujoMutationResponse {
  data: Flujo;
  pair?: FlujoPair;
}

export async function crearFlujo(
  payload: FlujoCreatePayload,
): Promise<FlujoMutationResponse> {
  const res = await apiFetch<FlujoMutationResponse>(
    "/finanzas/empresa/expenses",
    { method: "POST", body: JSON.stringify(payload) },
  );
  if (!res?.data) throw new Error("Error creando flujo");
  return res;
}

export async function actualizarFlujo(
  id: number | string,
  payload: FlujoUpdatePayload,
): Promise<Flujo> {
  const res = await apiFetch<{ data: Flujo }>(
    `/finanzas/empresa/expenses/${id}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
  if (!res?.data) throw new Error("Error actualizando flujo");
  return res.data;
}

/**
 * Round 2026-05-13 — endpoint dedicado para editar un intermediario:
 * actualiza ambos lados del par (pagador externo del lado 'recibe' +
 * receptor externo del lado 'paga' + campos compartidos) en una sola
 * transacción atómica del backend.
 *
 * El `id` puede ser el del lado 'recibe' o el del lado 'paga' — el
 * backend resuelve cuál es cuál vía `paired_id`.
 */
export async function actualizarFlujoIntermediario(
  id: number | string,
  payload: FlujoUpdatePayload,
): Promise<FlujoMutationResponse> {
  const res = await apiFetch<FlujoMutationResponse>(
    `/finanzas/empresa/expenses/intermediario/${id}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
  if (!res?.data) throw new Error("Error actualizando intermediario");
  return res;
}

/**
 * Elimina un flujo. Devuelve la lista de ids efectivamente borrados por el
 * backend (round 2026-06-05): para un intermediario son sus 2 mitades; el
 * backend borra el cluster completo aunque el vínculo `paired_id` esté roto.
 * El frontend usa estos ids para limpiar el state exacto, sin adivinar.
 */
export async function eliminarFlujo(
  id: number | string,
): Promise<Array<number | string>> {
  const res = await apiFetch<{ ok: boolean; deletedIds?: Array<number | string> }>(
    `/finanzas/empresa/expenses/${id}`,
    { method: "DELETE" },
  );
  if (!res?.ok) throw new Error("Error eliminando flujo");
  return Array.isArray(res.deletedIds) ? res.deletedIds : [];
}

// ── Adjuntos del flujo (fotos y documentos) ─────────────────────────────────

/**
 * FileEntry de un adjunto de flujo tal como lo devuelve
 * `storageService.attachUrls()` del backend. Mismo shape que el resto de los
 * módulos con adjuntos (info-huésped, tareas, documentos). Los campos `url` y
 * `thumbUrl` se firman al leer (NO se persisten) y caducan en 6h — por eso
 * FlujoFilesManager re-fetcha en cada apertura del modal.
 */
export interface FlujoFile {
  provider: "local";
  key: string;
  thumbKey: string | null;
  /** URL firmada absoluta (tamaño completo). */
  url: string;
  /** URL firmada del thumbnail (solo imágenes/videos). */
  thumbUrl: string | null;
  originalName: string;
  mimeType: string;
  size: number;
  /** Tipo lógico — decide cómo renderiza la galería. */
  kind: "image" | "video" | "doc";
  /** URL firmada de la versión liviana del video (480p); `null` si no existe en disco. */
  previewUrl?: string | null;
  /** Peso (bytes) de la versión liviana, si existe. */
  previewSize?: number | null;
}

/**
 * GET /finanzas/empresa/expenses/:id/images — lista los adjuntos del flujo con
 * URLs firmadas frescas. Se llama al abrir el modal de edición.
 */
export async function listarFlujoArchivos(flujoId: number | string): Promise<FlujoFile[]> {
  const res = await apiFetch<{ success: boolean; data: FlujoFile[] }>(
    `/finanzas/empresa/expenses/${flujoId}/images`,
    { method: "GET", errorMsg: "Error al cargar los adjuntos del flujo" },
  );
  return Array.isArray(res?.data) ? res!.data : [];
}

/**
 * POST /finanzas/empresa/expenses/:id/images — sube un adjunto (FormData
 * multipart, campo "file"). Devuelve el FileEntry con URLs firmadas listas
 * para renderizar. Sin `successMsg`: FlujoFilesManager arma su propio toast
 * con el nombre del archivo (útil al arrastrar varios a la vez).
 */
export async function subirFlujoArchivo(
  flujoId: number | string,
  file: File,
): Promise<FlujoFile | null> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiFetch<{ success: boolean; data: string; item: FlujoFile }>(
    `/finanzas/empresa/expenses/${flujoId}/images`,
    { method: "POST", body: formData, errorMsg: "Error al subir el archivo" },
  );
  return res?.item ?? null;
}

/**
 * DELETE /finanzas/empresa/expenses/:id/images?key=<key> — elimina un adjunto.
 * Devuelve la lista actualizada (con URLs re-firmadas) para sincronizar el
 * estado sin un segundo GET.
 */
export async function eliminarFlujoArchivo(
  flujoId: number | string,
  key: string,
): Promise<FlujoFile[]> {
  const res = await apiFetch<{ success: boolean; items: FlujoFile[] }>(
    `/finanzas/empresa/expenses/${flujoId}/images?key=${encodeURIComponent(key)}`,
    {
      method: "DELETE",
      successMsg: "Adjunto eliminado",
      errorMsg: "Error al eliminar el adjunto",
    },
  );
  return Array.isArray(res?.items) ? res!.items : [];
}

/**
 * Construye la URL de descarga forzada (`?d=1` → Content-Disposition:
 * attachment) a partir de la URL firmada del adjunto.
 */
export function buildFlujoDownloadUrl(file: FlujoFile): string {
  const sep = file.url.includes("?") ? "&" : "?";
  return `${file.url}${sep}d=1`;
}

// ── Catálogo property-centric de propietarios (selector del modal) ──────────

/**
 * Una propiedad con su CLIENTE-gestor (con login) y sus propietarios legales.
 * Alimenta el selector de contraparte del modal de Flujos: el admin elige una
 * PROPIEDAD y ve a su cliente inline ("Nombre Propiedad — Cliente").
 *
 * Round 2026-06-20 (decisión del owner): el selector muestra el **cliente**
 * (quien AF liquida), no el dueño legal. `cliente` puede ser `null` si la
 * propiedad no tiene cliente asignado ("(sin cliente)"). `propietarios` se
 * conserva por compat / uso futuro. Regla canónica: todo flujo de cliente se
 * ancla a su propiedad (flujo-financiero.md §10.3.1).
 */
export interface PropiedadConPropietarios {
  propiedadId: number;
  nombrePropiedad: string;
  propietarios: Array<{ id: number; nombre: string }>;
  cliente: { id: number; nombre: string } | null;
}

/** Trae el catálogo property-centric de propietarios para el modal. */
export async function listarPropietariosPropiedades(): Promise<
  PropiedadConPropietarios[]
> {
  const res = await apiFetch<{ data: PropiedadConPropietarios[] }>(
    "/finanzas/empresa/propietarios-propiedades",
    { method: "GET" },
  );
  return Array.isArray(res?.data) ? res!.data : [];
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Etiqueta humana para un `rol_de_aloja_facil`. */
export function rolToTipoFlujo(
  rol: Flujo["rolDeAlojaFacil"],
): { label: string; color: string; icon: string } {
  switch (rol) {
    case "paga":
      return { label: "Gasto", color: "#cf1322", icon: "💳" };
    case "recibe":
      return { label: "Ingreso", color: "#389e0d", icon: "💵" };
    case "intermediario":
      return { label: "Intermediario", color: "#1677ff", icon: "🔁" };
    default:
      return { label: "—", color: "#888", icon: "?" };
  }
}

/** Etiqueta humana para un `tipo` (esporadico vs regular). */
export function frecuenciaToBadge(
  tipo: Flujo["tipo"],
): { label: string; color: string } {
  return tipo === "regular"
    ? { label: "Regular", color: "purple" }
    : { label: "Esporádico", color: "default" };
}

/** Construye la `FlujoParty` para AF Empresa (tipo=0). Se usa como pagador
 *  fijo en "Nuevo gasto" y como receptor fijo en "Nuevo ingreso". */
export function pagoEntidadAF(): FlujoParty {
  return { tipo: 0, nombre: "Aloja Fácil", usuario_id: null };
}

/** Strippea comillas literales (simples o dobles) del inicio / fin del
 *  string. Defensa contra datos legacy en `users.nombre_propietario` o
 *  `users.nombre_usuario` que en algún momento se importaron escapando
 *  el JSON y quedaron con `"..."` o `'...'` como parte del valor. No
 *  toca comillas internas (apellidos compuestos con apóstrofo, etc.). */
function stripWrappingQuotes(s: string): string {
  const t = s.trim();
  if (t.length >= 2) {
    const first = t[0];
    const last = t[t.length - 1];
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return t.slice(1, -1).trim();
    }
  }
  return t;
}

/** Mapea un tipo numérico de PagoEntidad a su label legible (para
 *  pintar la celda "Pagado por" / "Pagado a" en la tabla).
 *
 *  Round 2026-06-01 — strippea comillas wrapper del `nombre` (data
 *  legacy a veces venía con `"Miguel Borges"` literal). */
export function pagoEntidadLabel(p: FlujoParty | null): string {
  if (!p) return "—";
  if (p.nombre && p.nombre.trim()) return stripWrappingQuotes(p.nombre);
  switch (p.tipo) {
    case 0:
      return "Aloja Fácil";
    case 1:
      // tipo=1 = cliente-gestor (regla canónica §10.3.1); el dueño legal no es
      // la contraparte de plata. Solo se usa como fallback si el row no trae
      // `nombre` (legacy).
      return "Cliente";
    case 2:
      return "Arrendatario";
    case 3:
      return "Aseador";
    case 4:
      return "Trabajador";
    case 5:
      return "Receptor externo";
    default:
      return "—";
  }
}
